/*
 * cart-tier-progress.js — keeps the cart drawer's rewards progress bar live.
 *
 * HAND-WRITTEN. Unlike most files in assets/, this is NOT esbuild output — there is no
 * src/ entry for it. Edit it here.
 *
 * Liquid renders the bar correct for the cart as the server knows it, and also hands over the
 * tier table (data-tiers) plus the copy templates. From then on this file owns the displayed
 * value, because the drawer does NOT re-render on every mutation:
 *
 *   - Removing a line takes the optimistic path in cart-drawer.js (card.remove() +
 *     patchCartPrices, no refreshCart), so .cart__body is never replaced. Watching for the
 *     section swap alone leaves the bar stale on every removal.
 *   - Quantity changes are debounced ~1.8s before the subtotal settles, so reading the
 *     drawer's own data-total-cents is far too slow to feel connected to the tap.
 *   - Add-on switches (Safe Ship, Mystery Item) never touch the cart while browsing — they are
 *     only pushed at checkout — so the server value never reflects them at all.
 *
 * So the displayed value is:  base + optimistic + addons
 *
 *   base       server truth, from the bar's data-count; re-read on every section render
 *   optimistic transient delta applied the moment a stepper/remove is tapped, cleared as soon
 *              as an authoritative number arrives
 *   addons     sum of the add-on switches currently on
 *
 * Both authoritative signals (a section render, or the drawer's own subtotal settling) reset
 * `optimistic` to 0, so the bar always converges on the Subtotal shown directly below it.
 */
(() => {
  const BAR = "[data-cart-tier-progress]";
  const UNLOCK_CLASS = "is-just-unlocked";
  const UNLOCK_MS = 600;

  /*
   * Tier actions, keyed by a marker's data-tier-action. Crossing a threshold currently changes
   * the bar and nothing else — the real discount is expected to be an Automatic Discount in
   * Shopify admin whose threshold matches the tier, so the bar only ever describes what
   * checkout will really do.
   *
   * To give a tier a behaviour: add an entry here, then add a matching option to the
   * `action` select on the reward_tier block in sections/cart-drawer.liquid.
   */
  const TIER_ACTIONS = {
    none() {},
  };

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  let base = null;
  let optimistic = 0;
  let lastValue = null;
  let lastProgress = null;
  let lastAuthoritative = null;
  let lastBarEl = null;

  const bar = () => document.querySelector(BAR);
  const int = (v) => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? 0 : n;
  };

  const config = (el) => {
    let tiers = [];
    try {
      tiers = JSON.parse(el.dataset.tiers || "[]");
    } catch (e) {
      tiers = [];
    }
    return {
      metric: el.dataset.metric === "quantity" ? "quantity" : "subtotal",
      max: int(el.dataset.max),
      tiers,
      moneyFormat: el.dataset.moneyFormat || "${{amount}}",
      message: el.dataset.message || "",
      completeMessage: el.dataset.completeMessage || "",
    };
  };

  const formatMoney = (cents, fmt) =>
    fmt
      .replace("{{amount}}", (cents / 100).toFixed(2))
      .replace("{{amount_no_decimals}}", String(Math.round(cents / 100)));

  // Add-ons are display-only until checkout, so their contribution is read off the switches.
  // Under the subtotal metric that is their price; under the quantity metric, one unit each.
  const addonDelta = (metric) => {
    let sum = 0;
    document
      .querySelectorAll(".cart-payment-options__add-on-row")
      .forEach((row) => {
        const on = row.querySelector(".switch");
        if (!on || on.getAttribute("aria-checked") !== "true") return;
        sum += metric === "quantity" ? 1 : int(row.dataset.addonPriceCents);
      });
    return sum;
  };

  const render = () => {
    const el = bar();
    if (!el || base === null) return;

    const cfg = config(el);
    const value = Math.max(0, base + optimistic + addonDelta(cfg.metric));
    if (value === lastValue) return;
    lastValue = value;

    const progress = cfg.max > 0 ? Math.min(100, Math.floor((value * 100) / cfg.max)) : 0;

    // Next reward = the smallest threshold not yet met. Order-independent, like the Liquid.
    const unmet = cfg.tiers.filter((t) => t.t > value).sort((a, b) => a.t - b.t);
    const next = unmet[0] || null;
    const complete = !next;

    el.classList.toggle("cart-tier-progress--complete", complete);

    // Copy. [reward] is emitted as its own element so it can be emphasised, matching the Liquid.
    const msgEl = el.querySelector(".cart-tier-progress__message");
    if (msgEl) {
      let text = cfg.completeMessage;
      if (!complete) {
        const remaining = next.t - value;
        const amount =
          cfg.metric === "quantity" ? String(remaining) : formatMoney(remaining, cfg.moneyFormat);
        text = cfg.message
          .split("[amount]").join(amount)
          .split("[count]").join(String(remaining));
      }
      const parts = text.split("[reward]");
      msgEl.textContent = parts[0];
      if (!complete && parts.length > 1) {
        const strong = document.createElement("strong");
        strong.className = "cart-tier-progress__reward";
        strong.textContent = next.reward;
        msgEl.appendChild(strong);
        msgEl.appendChild(document.createTextNode(parts[1]));
      }
    }

    const track = el.querySelector(".cart-tier-progress__track");
    if (track) track.setAttribute("aria-valuenow", String(progress));

    // Milestones, plus a pulse on any threshold crossed by this update.
    el.querySelectorAll("[data-threshold]").forEach((marker) => {
      const threshold = int(marker.dataset.threshold);
      const reached = value >= threshold;
      const wasReached = marker.classList.contains("is-reached");
      marker.classList.toggle("is-reached", reached);

      if (reached && !wasReached && lastProgress !== null) {
        marker.classList.add(UNLOCK_CLASS);
        window.setTimeout(() => marker.classList.remove(UNLOCK_CLASS), UNLOCK_MS);
        const action = TIER_ACTIONS[marker.dataset.tierAction];
        if (action) action(marker, el);
      }
    });

    // The element is replaced on a section render, so a CSS transition would have nothing to
    // animate from. Re-apply the previous width, flush it, then set the new one.
    if (lastProgress !== null && lastProgress !== progress && !reduceMotion.matches) {
      el.style.setProperty("--tier-progress", `${lastProgress}%`);
      void el.offsetWidth;
      requestAnimationFrame(() => el.style.setProperty("--tier-progress", `${progress}%`));
    } else {
      el.style.setProperty("--tier-progress", `${progress}%`);
    }

    el.dataset.progress = String(progress);
    lastProgress = progress;
  };

  /*
   * The freshest authoritative number in the DOM, or null if none is available.
   *
   * The drawer maintains these itself through every mutation path, including the optimistic
   * removal that skips the section render, so they stay ahead of the bar's own data-count —
   * which only moves when Liquid re-renders. Reading them is also what guarantees the bar can
   * never disagree with the Subtotal printed right below it: same number, same source.
   *
   * Both are add-on-free, matching cart-totals: data-total-cents starts from cart-totals[0],
   * and syncCartCount subtracts add-on units. The switch handler in cart-drawer.js does add
   * add-on prices into data-total-cents, so that much is taken back out here and re-applied in
   * render() — otherwise toggling Safe Ship would count twice.
   */
  const readAuthoritative = (metric) => {
    if (metric === "quantity") {
      const title = document.querySelector(".cart-item-container__title[data-item-count]");
      return title ? int(title.dataset.itemCount) : null;
    }
    const total = document.querySelector(".cart-payment-options__price--final");
    return total ? int(total.dataset.totalCents) - addonDelta("subtotal") : null;
  };

  // An authoritative number has landed: trust it and drop any optimistic guess.
  const adoptServerValue = (force) => {
    const el = bar();
    if (!el) return;

    const live = readAuthoritative(config(el).metric);
    const next = live === null ? int(el.dataset.count) : live;

    // A section render replaces the bar, which is always a full resync.
    const replaced = el !== lastBarEl;
    lastBarEl = el;
    if (replaced) lastValue = null;

    /*
     * Otherwise, ignore mutations that did not actually move the authoritative number. The
     * drawer touches the DOM constantly while a change is in flight — hiding the removed card,
     * toggling loading classes, swapping upsell carousels — and every one of those fires the
     * observer at a moment when the subtotal is still the old value. Clearing the optimistic
     * delta there makes the bar snap back to where it was and then forward again.
     */
    if (!force && !replaced && next === lastAuthoritative) return;

    lastAuthoritative = next;
    base = next;
    optimistic = 0;
    render();
  };

  const applyOptimistic = (delta) => {
    if (!delta || base === null) return;
    optimistic += delta;
    render();
  };

  const cardValue = (card, metric) => {
    if (metric === "quantity") {
      const input = card.querySelector("input[type='number'], .stepper__input");
      return Math.max(1, int(input && input.value) || 1);
    }
    return int(card.dataset.finalCents);
  };

  const onClick = (e) => {
    const el = bar();
    if (!el) return;
    const metric = config(el).metric;

    // Add-on switches: recompute from switch state. switch.js flips aria-checked after this
    // delegated handler runs, so read the post-click state on the next frame.
    if (e.target.closest(".cart-payment-options__add-on-row .switch")) {
      requestAnimationFrame(() => {
        lastValue = null;
        render();
      });
      return;
    }

    const card = e.target.closest(".cart-item-card");
    if (!card) return;

    if (e.target.closest("[data-cart-item-remove], .cart-item-card__remove")) {
      applyOptimistic(-cardValue(card, metric));
      return;
    }

    const stepBtn = e.target.closest(".stepper__button, .stepper button");
    if (!stepBtn) return;

    const input = card.querySelector("input[type='number'], .stepper__input");
    const qty = Math.max(1, int(input && input.value) || 1);
    // stepper.js updates the input after this handler, so infer direction from the button.
    const label = `${stepBtn.className} ${stepBtn.getAttribute("aria-label") || ""}`.toLowerCase();
    const dir = /minus|decrease|decrement|down/.test(label) ? -1 : 1;
    if (dir < 0 && qty <= 1) return;

    const unit = metric === "quantity" ? 1 : Math.round(int(card.dataset.finalCents) / qty);
    applyOptimistic(dir * unit);
  };

  const start = () => {
    const root = document.querySelector(".cart");
    if (!root) return;

    adoptServerValue(true);
    root.addEventListener("click", onClick, true);

    // Section renders replace .cart__body / .cart__footer wholesale; the drawer's own subtotal
    // settling after the debounce shows up as a data-total-cents change. Either means the
    // server has spoken, so re-sync and drop the optimistic delta.
    new MutationObserver(() => adoptServerValue()).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-total-cents", "data-item-count"],
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
